;(() => {
  const scrollValueTargets = [];

  const addScrollValue = (element, property, from, to, progress) => {
    if (element instanceof HTMLElement) scrollValueTargets.push({ element, property, from, to, progress });
  };

  const clamp01 = (value) => Math.min(Math.max(value, 0), 1);

  const crossProgress = (element, startTop = window.innerHeight, endRatio = 0.5) => {
    const rect = element.getBoundingClientRect();
    const end = (window.innerHeight - rect.height) * endRatio;
    return startTop > end ? clamp01((startTop - rect.top) / (startTop - end)) : 1;
  };

  const onScrollFrame = (update, target = document) => {
    let ticking = false;
    const queue = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        update();
      });
    };
    target.addEventListener("scroll", queue, { passive: true });
    return queue;
  };

  const initTailoringCanvas = () => {
    const FAR_SCALE = 0.88;
    const NEAR_SCALE = 1.08;
    const canvasWindow = document.querySelector(".about-built-on-tailoring__list");
    if (!(canvasWindow instanceof HTMLElement)) return;
    const runway = canvasWindow.closest(".about-built-on-tailoring-wrap");
    const section = canvasWindow.parentElement;

    let items = [];
    let travel = 0;
    const pin = { length: 0, top: 0, flow: 0, base: null };

    const measure = () => {
      for (const { el } of items) el.style.removeProperty("--drift");
      items = [];
      let canvasBottom = 0;
      let bottomSpeed = 1;
      const mobile = window.matchMedia("(max-width: 1000px)").matches;
      for (const el of canvasWindow.querySelectorAll(".about-built-on-tailoring__list-item")) {
        if (!(el instanceof HTMLElement) || el.offsetParent === null) continue;
        const attr = (mobile && el.getAttribute("data-speed-mobile")) || el.getAttribute("data-speed") || "100";
        items.push({ el, speed: parseFloat(attr) / 100, scale: 1 });
      }
      const speeds = items.map((item) => item.speed);
      const [min, max] = [Math.min(...speeds), Math.max(...speeds)];
      for (const item of items) {
        const depth = max === min ? 1 : (item.speed - min) / (max - min);
        item.scale = FAR_SCALE + depth * (NEAR_SCALE - FAR_SCALE);
        const bottom = item.el.offsetTop + (item.el.offsetHeight * (1 + item.scale)) / 2;
        if (bottom > canvasBottom) {
          canvasBottom = bottom;
          bottomSpeed = item.speed;
        }
      }
      const hiddenCanvas = canvasBottom - canvasWindow.clientHeight;
      travel = Math.max(hiddenCanvas / bottomSpeed, 0);
      pin.base = null;
      if (section instanceof HTMLElement && getComputedStyle(canvasWindow).position === "sticky") {
        const content = section.querySelector(".about-built-on-tailoring__content");
        pin.flow = content instanceof HTMLElement ? content.offsetTop + content.offsetHeight : canvasWindow.offsetTop;
        pin.length = section.clientHeight - pin.flow - canvasWindow.offsetHeight;
        pin.top = parseFloat(getComputedStyle(canvasWindow).top) || 0;
        pin.base = section;
      } else if (section instanceof HTMLElement && runway instanceof HTMLElement) {
        pin.flow = 0;
        pin.length = runway.clientHeight - section.offsetHeight;
        pin.top = parseFloat(getComputedStyle(section).top) || 0;
        pin.base = runway;
      }
    };

    const update = () => {
      if (items.length === 0) return;
      let t = 0;
      if (pin.base && pin.length > 0) {
        t = clamp01((pin.top - (pin.base.getBoundingClientRect().top + pin.flow)) / pin.length);
      } else {
        const start = window.innerHeight - canvasWindow.clientHeight / 2;
        t = clamp01((start - canvasWindow.getBoundingClientRect().top) / (start + canvasWindow.clientHeight));
      }
      for (const { el, speed, scale } of items) {
        el.style.setProperty("--drift", `translateY(${(-t * travel * speed).toFixed(1)}px) scale(${scale.toFixed(3)})`);
      }
    };

    measure();
    update();
    const queue = onScrollFrame(update);
    window.addEventListener("resize", () => {
      measure();
      queue();
    });
  };

  const initScrollValues = () => {
    const DESIGN_OPENER_CROP = 1.227;
    const DESIGN_Y1948_CROP = 1.27;
    const DESIGN_Y1948_REST = 1.04;
    const DESIGN_STRIP_CROP = 1.187;
    const DESIGN_NEW_GEN_CROP = 1.206;

    const heroFrame = document.querySelector(".about-who-we-are__image-container");
    if (heroFrame instanceof HTMLElement) {
      addScrollValue(heroFrame.querySelector(".about-who-we-are__image"), "--zoom", DESIGN_OPENER_CROP, 1, () =>
        clamp01(window.scrollY / heroFrame.offsetHeight),
      );
    }

    const y1948Frame = document.querySelector(".about-y1948__image-container");
    if (y1948Frame instanceof HTMLElement) {
      addScrollValue(y1948Frame.querySelector(".about-y1948__image"), "--zoom", DESIGN_Y1948_CROP, DESIGN_Y1948_REST, () =>
        crossProgress(y1948Frame),
      );
    }

    const y1948MobileFrame = document.querySelector(".about-y1948-mobile__frame");
    if (y1948MobileFrame instanceof HTMLElement) {
      addScrollValue(y1948MobileFrame.querySelector(".about-y1948-mobile__image"), "--zoom", DESIGN_OPENER_CROP, 1, () =>
        crossProgress(y1948MobileFrame),
      );
    }

    const stripBand = document.querySelector(".about-y1948-scroll-band");
    if (stripBand instanceof HTMLElement) {
      addScrollValue(stripBand.querySelector(".about-y1948-scroll"), "--zoom", DESIGN_STRIP_CROP, 1, () =>
        crossProgress(stripBand),
      );
    }

    const values = document.querySelector(".about-our-values");
    const timeline = document.querySelector(".about-logo-history");
    const pinWrap = document.querySelector(".about-logo-history-wrap");
    if (timeline instanceof HTMLElement && pinWrap instanceof HTMLElement) {
      const PIN_RUNWAY = 1.5;
      const APPROACH_SHARE = (496 - 213) / 1027;
      const measurePin = () => pinWrap.style.setProperty("--pin-runway", `${window.innerHeight * PIN_RUNWAY}px`);
      measurePin();
      window.addEventListener("resize", measurePin);
      const pinTop = () => (window.innerHeight - timeline.offsetHeight) / 2;
      const pinProgress = () => {
        const travel = pinWrap.clientHeight - timeline.offsetHeight;
        return travel > 0 ? clamp01((pinTop() - pinWrap.getBoundingClientRect().top) / travel) : 0;
      };
      addScrollValue(timeline, "--reveal", 0, 1, () => {
        const approach = clamp01((window.innerHeight - timeline.getBoundingClientRect().top) / (window.innerHeight - pinTop()));
        return APPROACH_SHARE * approach + (1 - APPROACH_SHARE) * pinProgress();
      });
      if (values instanceof HTMLElement) {
        addScrollValue(values, "--settle", 1, 0, () => {
          if (getComputedStyle(timeline).position !== "sticky") return crossProgress(values);
          const rect = values.getBoundingClientRect();
          return clamp01((window.innerHeight - rect.top) / (window.innerHeight - pinTop() + rect.height));
        });
      }
    }

    const newGeneration = document.querySelector(".about-new-generation");
    const carousel = document.querySelector(".about-new-generation__carousel");
    const newGenFrame = document.querySelector(".about-new-generation__image-container");
    if (newGeneration instanceof HTMLElement) {
      if (carousel instanceof HTMLElement) {
        addScrollValue(carousel, "--arrive", 1, 0, () => crossProgress(newGeneration));
      }
      if (newGenFrame instanceof HTMLElement) {
        addScrollValue(newGenFrame.querySelector(".about-new-generation__image"), "--zoom", DESIGN_NEW_GEN_CROP, 1, () => {
          const frameInset = newGenFrame.getBoundingClientRect().top - newGeneration.getBoundingClientRect().top;
          return crossProgress(newGeneration, window.innerHeight - frameInset);
        });
      }
    }

    const evolving = document.querySelector(".about-evolving-the-standard");
    const evolvingPanel = document.querySelector(".about-evolving-the-standard__panel");
    if (evolving instanceof HTMLElement && evolvingPanel instanceof HTMLElement) {
      addScrollValue(evolving, "--settle", 1, 0, () => crossProgress(evolvingPanel));
    }

    const footerBand = document.querySelector(".about-image-band");
    if (footerBand instanceof HTMLElement) {
      addScrollValue(footerBand.querySelector(".about-image-band__image"), "--zoom", DESIGN_OPENER_CROP, 1, () =>
        crossProgress(footerBand),
      );
    }

    if (scrollValueTargets.length === 0) return;

    const update = () => {
      for (const { element, property, from, to, progress } of scrollValueTargets) {
        element.style.setProperty(property, (from - (from - to) * progress()).toFixed(4));
      }
    };
    update();
    onScrollFrame(update);
  };

  const initLogoGallery = () => {
    const gallery = document.querySelector(".about-logo-history__list");
    if (!(gallery instanceof HTMLElement)) return;
    const years = [];
    for (const year of gallery.querySelectorAll(".about-logo-history__logo-year")) {
      if (year instanceof HTMLElement) years.push(year);
    }
    if (years.length === 0) return;
    const update = () => {
      const group = gallery.clientHeight;
      const showing = group > 0 ? Math.round(gallery.scrollTop / group) : 0;
      for (let i = 0; i < years.length; i++) {
        years[i].style.setProperty("--fade", i === showing ? "1" : "0");
      }
    };
    update();
    onScrollFrame(update, gallery);
  };

  const initTheNewGenerationScroll = () => {
    const SplideLib = window.Splide;
    if (!SplideLib) return;
    const SLIDE_VW = (293 / 1440) * 100;
    for (const slider of document.querySelectorAll(".about-new-generation__gallery")) {
      new SplideLib(slider, {
        arrows: false,
        pagination: false,
        type: "loop",
        drag: true,
        fixedWidth: `${SLIDE_VW}vw`,
        autoScroll: { speed: 1, pauseOnHover: false },
        breakpoints: {
          1000: { fixedWidth: "min(18.3125rem, calc(293 / 390 * 100vw))", autoScroll: { speed: 0.5, pauseOnHover: false } },
        },
      }).mount(window.splide.Extensions);
    }
  };

  initTheNewGenerationScroll();
  initLogoGallery();

  if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    initScrollValues();
    initTailoringCanvas();
  }
})();
